// src/components/family/profile/ChildProfileTabs.tsx
// Tab navigation for child profile sections

import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Activity,
  FileText,
  Target,
  Users,
  Calendar,
  Pill,
  type LucideIcon,
} from 'lucide-react';

interface TabItem {
  id: string;
  label: string;
  shortLabel?: string;
  icon: LucideIcon;
  path: string;
  badge?: number;
}

interface ChildProfileTabsProps {
  childId: string;
  badges?: Record<string, number>;
  className?: string;
}

export const ChildProfileTabs: React.FC<ChildProfileTabsProps> = ({
  childId,
  badges = {},
  className = '',
}) => {
  const location = useLocation();
  const basePath = `/family/children/${childId}`;

  const tabs: TabItem[] = [
    {
      id: 'overview',
      label: 'Overview',
      shortLabel: 'Overview',
      icon: LayoutDashboard,
      path: basePath,
    },
    {
      id: 'health',
      label: 'Health Tracking',
      shortLabel: 'Health',
      icon: Activity,
      path: `${basePath}/health`,
      badge: badges.health,
    },
    {
      id: 'appointments',
      label: 'Appointments',
      shortLabel: 'Calendar',
      icon: Calendar,
      path: `${basePath}/appointments`,
      badge: badges.appointments,
    },
    {
      id: 'medications',
      label: 'Medications',
      shortLabel: 'Meds',
      icon: Pill,
      path: `${basePath}/medications`,
      badge: badges.medications,
    },
    {
      id: 'documents',
      label: 'Documents',
      shortLabel: 'Docs',
      icon: FileText,
      path: `${basePath}/documents`,
      badge: badges.documents,
    },
    {
      id: 'goals',
      label: 'Goals & Progress',
      shortLabel: 'Goals',
      icon: Target,
      path: `${basePath}/goals`,
      badge: badges.goals,
    },
    {
      id: 'care-team',
      label: 'Care Team',
      shortLabel: 'Team',
      icon: Users,
      path: `${basePath}/care-team`,
    },
  ];

  // Check if this is the overview tab (exact match on base path)
  const isOverviewActive = location.pathname === basePath || 
    location.pathname === `${basePath}/` ||
    location.pathname === `${basePath}/overview`;

  return (
    <div className={`relative ${className}`}>
      {/* Gradient fade edges for horizontal scroll */}
      <div className="absolute left-0 top-0 bottom-0 w-8 bg-gradient-to-r from-white to-transparent z-10 pointer-events-none lg:hidden" />
      <div className="absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-white to-transparent z-10 pointer-events-none lg:hidden" />

      <nav
        className="flex gap-1 overflow-x-auto scrollbar-hide px-1 -mx-1 lg:flex-wrap"
        role="tablist"
        aria-label="Profile sections"
      >
        {tabs.map((tab) => {
          const Icon = tab.icon;
          const isActive = tab.id === 'overview' 
            ? isOverviewActive 
            : location.pathname.startsWith(tab.path);

          return (
            <NavLink
              key={tab.id}
              to={tab.path}
              role="tab"
              aria-selected={isActive}
              className={`
                relative flex items-center gap-2 px-4 py-2.5 rounded-xl
                font-medium text-sm whitespace-nowrap
                transition-all duration-200 ease-out
                flex-shrink-0 group
                ${isActive
                  ? 'bg-sage text-white shadow-md shadow-sage/20'
                  : 'text-navy-light hover:bg-cream-dark hover:text-navy'
                }
              `}
            >
              <Icon 
                className={`w-4 h-4 flex-shrink-0 transition-transform duration-200 ${
                  isActive ? '' : 'group-hover:scale-110'
                }`} 
              />
              
              {/* Full label on larger screens */}
              <span className="hidden sm:inline">{tab.label}</span>
              
              {/* Short label on mobile */}
              <span className="sm:hidden">{tab.shortLabel || tab.label}</span>

              {/* Badge */}
              {tab.badge && tab.badge > 0 && (
                <span
                  className={`
                    absolute -top-1 -right-1 min-w-[18px] h-[18px] 
                    flex items-center justify-center
                    text-[10px] font-bold rounded-full
                    ${isActive 
                      ? 'bg-white text-sage' 
                      : 'bg-terracotta text-white'
                    }
                  `}
                >
                  {tab.badge > 99 ? '99+' : tab.badge}
                </span>
              )}

              {/* Active indicator line */}
              {isActive && (
                <span
                  className="absolute bottom-0 left-4 right-4 h-0.5 bg-white/40 rounded-full"
                  aria-hidden="true"
                />
              )}
            </NavLink>
          );
        })}
      </nav>
    </div>
  );
};

// Vertical tabs variant for sidebar layout
export const ChildProfileTabsVertical: React.FC<ChildProfileTabsProps> = ({
  childId,
  badges = {},
  className = '',
}) => {
  const location = useLocation();
  const basePath = `/family/children/${childId}`;

  const tabs: TabItem[] = [
    { id: 'overview', label: 'Overview', icon: LayoutDashboard, path: basePath },
    { id: 'health', label: 'Health Tracking', icon: Activity, path: `${basePath}/health`, badge: badges.health },
    { id: 'appointments', label: 'Appointments', icon: Calendar, path: `${basePath}/appointments`, badge: badges.appointments },
    { id: 'medications', label: 'Medications', icon: Pill, path: `${basePath}/medications`, badge: badges.medications },
    { id: 'documents', label: 'Documents', icon: FileText, path: `${basePath}/documents`, badge: badges.documents },
    { id: 'goals', label: 'Goals', icon: Target, path: `${basePath}/goals`, badge: badges.goals },
    { id: 'care-team', label: 'Care Team', icon: Users, path: `${basePath}/care-team` },
  ];

  const isOverviewActive = location.pathname === basePath || 
    location.pathname === `${basePath}/` ||
    location.pathname === `${basePath}/overview`;

  return (
    <nav className={`flex flex-col gap-1 ${className}`} role="tablist" aria-label="Profile sections">
      {tabs.map((tab) => {
        const Icon = tab.icon;
        const isActive = tab.id === 'overview' 
          ? isOverviewActive 
          : location.pathname.startsWith(tab.path);

        return (
          <NavLink
            key={tab.id}
            to={tab.path}
            role="tab"
            aria-selected={isActive}
            className={`
              relative flex items-center gap-3 px-4 py-3 rounded-xl
              font-medium text-sm
              transition-all duration-200 ease-out
              group
              ${isActive
                ? 'bg-sage text-white shadow-md shadow-sage/20'
                : 'text-navy-light hover:bg-cream-dark hover:text-navy'
              }
            `}
          >
            {/* Left accent bar */}
            <span
              className={`
                absolute left-0 top-2 bottom-2 w-1 rounded-full
                transition-all duration-200
                ${isActive ? 'bg-white/40' : 'bg-transparent group-hover:bg-sage/20'}
              `}
              aria-hidden="true"
            />

            <Icon 
              className={`w-5 h-5 flex-shrink-0 transition-transform duration-200 ${
                isActive ? '' : 'group-hover:scale-110'
              }`} 
            />
            
            <span className="flex-1">{tab.label}</span>

            {/* Badge */}
            {tab.badge && tab.badge > 0 && (
              <span
                className={`
                  min-w-[22px] h-[22px] 
                  flex items-center justify-center
                  text-xs font-bold rounded-full
                  ${isActive 
                    ? 'bg-white/20 text-white' 
                    : 'bg-terracotta/10 text-terracotta'
                  }
                `}
              >
                {tab.badge > 99 ? '99+' : tab.badge}
              </span>
            )}
          </NavLink>
        );
      })}
    </nav>
  );
};

export default ChildProfileTabs;
